<template>
    <Dialog :visible="visible" @update:visible="$emit('update:visible', $event)" modal header="Postback Details"
        :style="{ width: '80vw' }" class="p-fluid" :closeOnEscape="true" :closable="true">

        <div class="flex flex-col gap-4 card-bg rounded-lg p-3" v-if="siteLink">
            <div class="flex flex-col gap-2">
                <label for="registerPostbackUrl" class="block text-sm font-medium mb-2">Register Postback URL</label>

                <Skeleton v-if="isRegisterPostbackLoading" class="w-full h-8" v-tooltip.top="'Loading...'" />
                <!-- View Mode -->
                <div v-else-if="!isEditingRegister" class="flex items-center gap-2">
                    <div class="p-3 rounded-md cursor-pointer bg-gray-700 flex-grow" @click="toggleEditRegister"
                        v-tooltip.left="'Click to edit'">
                        <code id="register-postback-url">{{ registerPostbackUrl || 'No register postback URL set' }}</code>
                    </div>
                    <div class="flex gap-1">
                        <Button :icon="'pi pi-pencil'" @click="toggleEditRegister" class="p-button-text" severity="primary"
                            v-tooltip.top="'Edit Postback'" />
                        <Button v-if="registerPostbackUrl" icon="pi pi-copy" @click="copyToClipboard(registerPostbackUrl)"
                            class="p-button-text" severity="secondary" outlined v-tooltip.top="'Copy URL'"
                            :disabled="!registerPostbackUrl" />
                    </div>
                </div>

                <!-- Edit Mode -->
                <div v-else class="flex flex-col gap-3">
                    <div class="field">
                        <div class="flex gap-2 items-start">
                            <InputText id="registerPostbackUrl" v-model="editedRegisterPostbackUrl" class="w-full"
                                placeholder="Enter your register postback URL" />
                            <div class="flex gap-1 shrink-0">
                                <Button icon="pi pi-check" @click="saveRegisterPostback" severity="primary" class="p-button-sm"
                                    v-tooltip.top="'Save Changes'" />
                                <Button icon="pi pi-times" @click="cancelEditRegister" class="p-button-sm" severity="secondary"
                                    outlined v-tooltip.top="'Cancel'" />
                            </div>
                        </div>
                        <small class="text-white mt-1">
                            Example: https://your-domain.com/postback?click_id={clickId}&et=register
                        </small>
                    </div>
                </div>
            </div>
            
            <div class="flex flex-col gap-2">
                <label for="postbackUrl" class="block text-sm font-medium mb-2">FTD Postback URL</label>

                <Skeleton v-if="isFtdPostbackLoading" class="w-full h-8" v-tooltip.top="'Loading...'" />
                <!-- View Mode -->
                <div v-else-if="!isEditingFTD" class="flex items-center gap-2">

                    <div class="p-3 rounded-md  cursor-pointer bg-gray-700 flex-grow" @click="toggleEdit"
                        v-tooltip.left="'Click to edit'">
                        <code id="ftd-postback-url">{{ ftdPostbackUrl || 'No postback URL set' }}</code>
                    </div>
                    <div class="flex gap-1">
                        <Button :icon="'pi pi-pencil'" @click="toggleEdit" class="p-button-text " severity="primary"
                            v-tooltip.top="'Edit Postback'" />
                        <Button v-if="ftdPostbackUrl" icon="pi pi-copy" @click="copyToClipboard(ftdPostbackUrl)"
                            class="p-button-text" severity="secondary" outlined v-tooltip.top="'Copy URL'"
                            :disabled="!ftdPostbackUrl" />
                    </div>
                </div>

                <!-- Edit Mode -->
                <div v-else class="flex flex-col gap-3">
                    <div class="field">
                        <div class="flex gap-2 items-start">
                            <InputText id="postbackUrl" v-model="editedFTDPostbackUrl" class="w-full "
                                placeholder="Enter your ftd postback URL" />
                            <div class="flex gap-1 shrink-0">
                                <Button icon="pi pi-check" @click="savePostback" severity="primary" class="p-button-sm"
                                    v-tooltip.top="'Save Changes'" />
                                <Button icon="pi pi-times" @click="cancelEdit" class="p-button-sm " severity="secondary"
                                    outlined v-tooltip.top="'Cancel'" />
                            </div>
                        </div>
                        <small class="text-white mt-1">
                            Example: https://your-domain.com/postback?click_id={clickId}&et=ftd
                        </small>
                    </div>
                </div>
            </div>



            <div class="flex flex-col gap-2">
                <h3 class="text-lg font-semibold">Available Parameters</h3>
                <ul class="list-disc list-inside">
                    <li>{clickId} - Unique click identifier</li>
                </ul>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end gap-2">
                <Button v-if="isEditingFTD || isEditingRegister" label="Cancel" @click="cancelEdit" severity="secondary" outlined />
                <Button v-if="isEditingFTD" label="Save FTD" @click="savePostback" severity="primary" />
                <Button v-if="isEditingRegister" label="Save Register" @click="saveRegisterPostback" severity="primary" />
                <Button v-else label="Close" @click="closeDialog" class="p-button-text" severity="secondary" outlined />
            </div>
        </template>
    </Dialog>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { useToast } from 'primevue/usetoast';
import SiteLinkService from '@/services/SiteLinkService';
const props = defineProps({
    visible: {
        type: Boolean,
        required: false
    },
    siteLink: {
        type: Object,
        required: false
    },
    affiliateSytem: {
        type: Object,
        required: false
    }
});

const emit = defineEmits(['update:visible', 'save']);

const toast = useToast();
const affiliateSystemId = ref(null);
const siteBrandLinkId = ref(null);
const isEditingFTD = ref(false);
const ftdPostbackUrl = ref(null); // This should be loaded from siteLink data
const editedFTDPostbackUrl = ref('');

const isFtdPostbackLoading = ref(true);

const registerPostbackUrl = ref(null);
const editedRegisterPostbackUrl = ref('');
const isEditingRegister = ref(false);
const isRegisterPostbackLoading = ref(true);

const setPostbackData = (siteLink) => {
    siteBrandLinkId.value = siteLink.linkId;
    affiliateSystemId.value = props.affiliateSytem._id;
    ftdPostbackUrl.value = siteLink.ftdPostback || '';
    editedFTDPostbackUrl.value = siteLink.ftdPostback || '';
    
    // Add register postback data
    registerPostbackUrl.value = siteLink.registerPostback || '';
    editedRegisterPostbackUrl.value = siteLink.registerPostback || '';
    
    isFtdPostbackLoading.value = false;
    isRegisterPostbackLoading.value = false;
};

watch(() => props.visible, (isVisible) => {
    if (!isVisible) {
        resetVariables();
    } else if (props.siteLink) {
        setPostbackData(props.siteLink);
    }
});

watch(() => props.siteLink, async (newSiteLink) => {
    if (newSiteLink) {
        setPostbackData(newSiteLink);
    }
}, { immediate: true });

const toggleEdit = () => {
    if (isEditingFTD.value) {
        savePostback();
    } else {
        editedFTDPostbackUrl.value = ftdPostbackUrl.value;
        isEditingFTD.value = true;
    }
};

const cancelEdit = () => {
    isEditingFTD.value = false;
    editedFTDPostbackUrl.value = ftdPostbackUrl.value;
};

const savePostback = async () => {
    try {

        await SiteLinkService.updateFTDPostback(siteBrandLinkId.value, editedFTDPostbackUrl.value);
        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Postback URL updated successfully',
            life: 3000
        });
        closeDialog();
        emit('save');
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update postback URL',
            life: 3000
        });
    }
};

const toggleEditRegister = () => {
    if (isEditingRegister.value) {
        saveRegisterPostback();
    } else {
        editedRegisterPostbackUrl.value = registerPostbackUrl.value;
        isEditingRegister.value = true;
    }
};

const cancelEditRegister = () => {
    isEditingRegister.value = false;
    editedRegisterPostbackUrl.value = registerPostbackUrl.value;
};

const saveRegisterPostback = async () => {
    try {
        await SiteLinkService.updateRegisterPostback(siteBrandLinkId.value, editedRegisterPostbackUrl.value);
        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Register postback URL updated successfully',
            life: 3000
        });
        closeDialog();
        emit('save');
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update register postback URL',
            life: 3000
        });
    }
};

const closeDialog = () => {
    emit('update:visible', false);
    resetVariables();
    isFtdPostbackLoading.value = false;
};

const resetVariables = () => {
    affiliateSystemId.value = null;
    siteBrandLinkId.value = null;
    ftdPostbackUrl.value = null;
    editedFTDPostbackUrl.value = '';
    isEditingFTD.value = false;
    
    // Add register postback reset
    registerPostbackUrl.value = null;
    editedRegisterPostbackUrl.value = '';
    isEditingRegister.value = false;
    isRegisterPostbackLoading.value = true;
}
const copyToClipboard = async (text) => {
    if (!text) return;
    try {
        await navigator.clipboard.writeText(text);
        toast.add({
            severity: 'success',
            summary: 'Copied!',
            detail: 'Postback URL copied to clipboard',
            life: 3000
        });
    } catch (err) {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to copy URL',
            life: 3000
        });
    }
};

</script>

<style scoped>

</style>